import type { ComponentProps } from 'react';
import React, { Fragment, memo } from 'react';
import PropTypes from 'prop-types';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import Spinner from '@atlaskit/spinner/spinner';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { VIEW_PAGE_EDIT_BUTTON_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { CollabServiceProperty } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { EDIT_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useNoEditAccessFlag } from '@confluence/edit-features/entry-points/useNoEditAccessFlag.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { EditContentButtonComponent } from './EditContentButtonComponent.tsx';
import { useEmbeddedMigrationQuery } from './useEmbeddedMigrationQuery.tsx';
import type {
	EditContentButtonQuery$data,
	EditContentButtonQuery$variables,
} from './__generated__/EditContentButtonQuery.graphql.ts';
import type {
	EditContentButton_content$data,
	EditContentButton_content$key,
} from './__generated__/EditContentButton_content.graphql.ts';
import { useFabricEligibilityFragment } from './FabricEligibilityFragmentContext.tsx';

const EditContentButtonQuery = graphql`
	query EditContentButtonQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				type
				operations {
					operation
					targetType
				}
				metadata {
					frontend {
						fabricEditorSupported
						embedded
						collabService
					}
				}
				subType
				type
				space {
					id
					key
				}
			}
		}
	}
`;

export const EditContentButtonFragment: GraphQLTaggedNode = graphql`
	fragment EditContentButton_content on Content {
		id
		type
		operations {
			operation
			targetType
		}
		metadata {
			frontend {
				fabricEditorSupported
				embedded
				collabService
			}
		}
		subType
		space {
			id
			key
		}
	}
`;

type EditContentButtonProps = Pick<
	ComponentProps<typeof EditContentButtonComponent>,
	'onRender'
> & {
	contentId: string;
	isCompanyHubButton?: boolean;
	contentFragmentRef?: EditContentButton_content$key | null;
};

type EditContentButtonContentData = Omit<EditContentButton_content$data, ' $fragmentType'>;

type EditContentButtonDataProps = EditContentButtonProps &
	WithFlagsProps & {
		contentData: EditContentButtonContentData | null | undefined;
		loading?: boolean;
		error?: Error;
	};

const EditContentButtonData = ({
	flags,
	contentId,
	isCompanyHubButton,
	contentData,
	loading = false,
	error,
	contentFragmentRef: _contentFragmentRef,
	...passThroughProps
}: EditContentButtonDataProps) => {
	const fabricEligibilityFragmentData = useFabricEligibilityFragment();
	const { epMigrationOnLoad, epMigrationOnClick } = useEmbeddedMigrationQuery(contentId, true);
	const {
		data: epMigrationOnLoadData,
		loading: epMigrationOnLoadLoading,
		error: epMigrationOnLoadError,
	} = epMigrationOnLoad;
	const {
		migrateEPData,
		data: epMigrationOnClickData,
		loading: epMigrationOnClickLoading,
		error: epMigrationOnClickError,
	} = epMigrationOnClick;
	const { showNoEditAccessFlag } = useNoEditAccessFlag(flags);

	let renderedEditContentButton: React.ReactNode;

	if (error) {
		renderedEditContentButton = <ErrorDisplay error={error} />;
	} else if (
		(!contentData && loading) ||
		(!epMigrationOnLoadData && epMigrationOnLoadLoading) ||
		(!epMigrationOnClickData && epMigrationOnClickLoading)
	) {
		renderedEditContentButton = <Spinner interactionName="edit-content-button" size="small" />;
	} else {
		const operations = contentData?.operations ?? [];
		const contentType = contentData?.type ?? undefined;
		const canEdit = operations.some(
			(operationCheckResult) =>
				operationCheckResult?.operation === 'update' &&
				operationCheckResult?.targetType === contentType,
		);

		if (!canEdit) {
			return (
				<GeneralShortcutListener accelerator={EDIT_SHORTCUT} listener={showNoEditAccessFlag} />
			);
		}

		const spaceKey = contentData?.space?.key ?? undefined;
		const metadataFrontend = contentData?.metadata?.frontend;
		const isFabricSupported = metadataFrontend?.fabricEditorSupported ?? false;
		const isNCSContent = metadataFrontend?.collabService === CollabServiceProperty.NCS;
		const isEmbeddedContent = (isEmbeddedConfluence_DO_NOT_USE() && isNCSContent) ?? false;
		const embeddedMigrationError = epMigrationOnLoadError || epMigrationOnClickError;

		return (
			<>
				{embeddedMigrationError && <ErrorDisplay error={embeddedMigrationError} />}
				<EditContentButtonComponent
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
					isCompanyHubButton={isCompanyHubButton}
					isFabricSupported={isFabricSupported}
					isEmbeddedContent={isEmbeddedContent}
					migrateEPData={!isNCSContent ? migrateEPData : undefined}
					fabricEligibilityFragmentData={fabricEligibilityFragmentData}
					{...passThroughProps}
				/>
			</>
		);
	}

	return (
		<Fragment>
			<ExperienceStart id={contentId} name={VIEW_PAGE_EDIT_BUTTON_EXPERIENCE} />
			{renderedEditContentButton}
		</Fragment>
	);
};

const EditContentButtonQueryReader = (props: EditContentButtonProps & WithFlagsProps) => {
	const { data, loading, error } = useQueryInterop<
		EditContentButtonQuery$data,
		EditContentButtonQuery$variables
	>(EditContentButtonQuery, {
		variables: { contentId: props.contentId },
		// Required so the edit button appears immediately after publishing a page.
		fetchPolicy: 'cache-and-network',
	});

	return (
		<EditContentButtonData
			{...props}
			contentData={data?.content?.nodes?.[0]}
			loading={loading}
			error={error}
		/>
	);
};

const EditContentButtonFragmentReader = (
	props: EditContentButtonProps &
		WithFlagsProps & { contentFragmentRef: EditContentButton_content$key },
) => {
	const contentData = useFragment(EditContentButtonFragment, props.contentFragmentRef);
	return <EditContentButtonData {...props} contentData={contentData} />;
};

const EditContentButtonInner = withFlags((props: EditContentButtonProps & WithFlagsProps) => {
	if (
		props.contentFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_edit_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<EditContentButtonFragmentReader {...props} contentFragmentRef={props.contentFragmentRef} />
		);
	}

	return <EditContentButtonQueryReader {...props} />;
});

export const EditContentButton: React.FC<EditContentButtonProps> = memo(
	withErrorBoundary({
		attribution: Attribution.BACKBONE,
	})(EditContentButtonInner),
);

EditContentButton.propTypes = {
	contentId: PropTypes.string.isRequired,
	isCompanyHubButton: PropTypes.bool,
	onRender: EditContentButtonComponent.propTypes?.onRender,
};
